/**
 * Generic enhancement executor that can call any microservice
 */
import type {
  EnhancementExecutionOptions,
  EnhancementMapping,
  EnhancementResult,
  EnhancementService,
} from "@/types/enhance";
import * as Sentry from "@sentry/react";

// Generic cache for enhancement results
// Key: serviceId + cacheKey from service.generateCacheKey
// Value: EnhancementResult
const enhancementCache = new Map<string, EnhancementResult>();

/**
 * Clear the enhancement cache for a specific service or all services
 */
export function clearEnhancementCache(serviceId?: string): void {
  if (serviceId) {
    // Clear cache entries for specific service
    for (const [key] of enhancementCache.entries()) {
      if (key.startsWith(`${serviceId}:`)) {
        enhancementCache.delete(key);
      }
    }
  } else {
    // Clear all cache
    enhancementCache.clear();
  }
}

/**
 * Get the current cache size (useful for debugging/monitoring)
 */
export function getEnhancementCacheSize(serviceId?: string): number {
  if (serviceId) {
    let count = 0;
    for (const [key] of enhancementCache.entries()) {
      if (key.startsWith(`${serviceId}:`)) {
        count++;
      }
    }
    return count;
  }
  return enhancementCache.size;
}

/**
 * Generate cache key automatically from requestFields and serviceOptions
 */
function generateCacheKey(
  service: EnhancementService,
  rowData: Record<string, any>,
  mapping: EnhancementMapping,
  serviceOptions?: Record<string, any>
): string | null {
  // If custom generateCacheKey is provided, use it (for backward compatibility)
  if (service.generateCacheKey) {
    const key = service.generateCacheKey(rowData, mapping, serviceOptions);
    return key ?? null;
  }

  // Automatic: generate cache key from requestColumns and serviceOptions
  const parts: string[] = [];

  // Add match mode to cache key
  if (mapping.matchMode) {
    parts.push(`matchMode:${mapping.matchMode}`);
  }

  // Add service options to cache key
  if (serviceOptions) {
    const sortedOptions = Object.keys(serviceOptions).sort();
    for (const key of sortedOptions) {
      parts.push(`${key}:${String(serviceOptions[key])}`);
    }
  }

  // Add request data to cache key
  if (Array.isArray(mapping.requestColumns)) {
    // Include all columns
    for (const col of mapping.requestColumns) {
      if (col && rowData[col] !== undefined && rowData[col] !== null) {
        const value = String(rowData[col]).trim();
        if (value) {
          parts.push(value);
        }
      }
    }
  }

  if (parts.length === 0) {
    return null; // No data to cache
  }

  return parts.join("|");
}

/**
 * Extract value from object using dot notation path
 * Supports arrays: "features[0].geometry.coordinates[1]"
 */
function extractValue(obj: any, path: string): any {
  const parts = path.split(".");
  let current = obj;

  for (const part of parts) {
    if (current === null || current === undefined) {
      return null;
    }

    // Handle array indices like "features[0]"
    const arrayMatch = part.match(/^(.+)\[(\d+)\]$/);
    if (arrayMatch) {
      const [, key, index] = arrayMatch;
      current = current[key]?.[parseInt(index, 10)];
    } else {
      current = current[part];
    }
  }

  return current;
}

/**
 * Build request body for a single row based on service configuration
 * Automatically builds from requestFields and apiFieldName
 */
function buildRowRequest(
  service: EnhancementService,
  rowData: Record<string, any>,
  mapping: EnhancementMapping,
  serviceOptions?: Record<string, any>
): { endpoint: string; query: any } | null {
  // If custom buildRequest is provided, use it (for backward compatibility)
  if (service.buildRequest) {
    // Map array format to fieldId -> [columns] mapping
    const requestData: Record<string, string[]> = {};
    if (service.requestFields.length > 0 && Array.isArray(mapping.requestColumns)) {
      const firstField = service.requestFields[0];
      requestData[firstField.id] = mapping.requestColumns;
    }

    const buildRequestArgs =
      service.buildRequest.length > 2
        ? [rowData, requestData, serviceOptions || mapping.serviceOptions]
        : [rowData, requestData];
    const result = (service.buildRequest as any)(...buildRequestArgs);
    if (result === null) {
      return null;
    }
    if (result && typeof result === "object" && "endpoint" in result) {
      return result as { endpoint: string; query: any };
    }
    return {
      endpoint: service.endpoint,
      query: result,
    };
  }

  // Automatic: build request from requestFields configuration
  const query: Record<string, any> = {};
  let hasData = false;

  // Combine all columns and custom values into first requestField's apiFieldName
  if (Array.isArray(mapping.requestColumns)) {
    const values: string[] = [];
    // Get all available column names from the first row to distinguish columns from custom values
    const availableColumns = Object.keys(rowData);
    
    for (const item of mapping.requestColumns) {
      if (!item) continue;
      
      // Check if it's a custom value (not a column name) - use original item to preserve spacing
      if (!availableColumns.includes(item)) {
        // Custom value - preserve exactly as user typed it
        values.push(item);
        hasData = true;
      } else {
        // Column name - get the value from the row data and trim it
        const trimmedItem = item.trim();
        if (
          rowData[trimmedItem] !== undefined &&
          rowData[trimmedItem] !== null &&
          rowData[trimmedItem] !== ""
        ) {
          const value = String(rowData[trimmedItem]).trim();
          if (value) {
            values.push(value);
            hasData = true;
          }
        }
      }
    }
    if (values.length > 0 && service.requestFields.length > 0) {
      const firstField = service.requestFields[0];
      query[firstField.apiFieldName] = values.join(" ");
    }
  }

  // Add default query parameters first
  if (service.defaultQueryParams) {
    Object.assign(query, service.defaultQueryParams);
  }

  // Add generic match mode if provided
  if (mapping.matchMode) {
    query.mode = mapping.matchMode;
  }

  // Then add service options (can override defaults and match mode)
  if (serviceOptions) {
    Object.assign(query, serviceOptions);
  }

  if (!hasData) {
    return null;
  }

  // Determine endpoint - use custom function if provided, otherwise use service endpoint
  const endpoint = service.getEndpoint
    ? service.getEndpoint(query)
    : service.endpoint;

  // If endpoint contains "/bulk", return endpoint/query format for bulk APIs
  // Otherwise return standard format
  if (service.endpoint.includes("/bulk") || endpoint.includes("/bulk")) {
    return {
      endpoint: endpoint.replace("/bulk", ""),
      query,
    };
  }

  return {
    endpoint,
    query,
  };
}

/**
 * Process response for a single row based on service configuration
 */
/**
 * Process response for a single row based on service configuration
 * Automatically extracts from responseFields and apiResponsePath
 */
function processRowResponse(
  service: EnhancementService,
  apiResponse: any,
  mapping: EnhancementMapping,
  responseFieldsCache?: Map<string, { apiResponsePath: string }>,
  serviceOptions?: Record<string, any>
): EnhancementResult["fields"] {
  // If custom processResponse is provided, use it (for backward compatibility)
  if (service.processResponse) {
    let responseData: Record<string, string[]> = {};
    const fieldIds = mapping.responseFields;
    for (let i = 0; i < fieldIds.length; i++) {
      const fieldId = fieldIds[i];
      responseData[fieldId] = [fieldId];
    }
    const processResponseArgs =
      service.processResponse.length > 2
        ? [apiResponse, responseData, serviceOptions || mapping.serviceOptions]
        : [apiResponse, responseData];
    return (service.processResponse as any)(...processResponseArgs);
  }

  // Automatic: extract values using responseFields and apiResponsePath
  // Handle wrapped response format (status/response wrapper)
  let response = apiResponse;
  if (apiResponse.status !== undefined && apiResponse.response) {
    if (apiResponse.status !== 200) {
      return { error: apiResponse.msg || `API error: ${apiResponse.status}` };
    }
    response = apiResponse.response;
  }

  const fields: Record<string, any> = {};
  const fieldIds = mapping.responseFields;

  // Use cached field paths if available
  if (responseFieldsCache) {
    for (let i = 0; i < fieldIds.length; i++) {
      const fieldId = fieldIds[i];
      const fieldInfo = responseFieldsCache.get(fieldId);
      if (fieldInfo) {
        fields[fieldId] = extractValue(response, fieldInfo.apiResponsePath);
      }
    }
  } else {
    // Find field and extract using apiResponsePath
    for (let i = 0; i < fieldIds.length; i++) {
      const fieldId = fieldIds[i];
      const field = service.responseFields.find((f) => f.id === fieldId);
      if (field) {
        fields[fieldId] = extractValue(response, field.apiResponsePath);
      }
    }
  }

  return fields;
}

/**
 * Execute enhancement for a batch of rows
 */
async function executeBatch(
  service: EnhancementService,
  data: Record<string, any>[],
  mapping: EnhancementMapping,
  options: Omit<EnhancementExecutionOptions, "mapping">
): Promise<EnhancementResult[]> {
  const serviceOptions = options.serviceOptions || mapping.serviceOptions;
  const baseUrl = service.baseUrl || "";
  const endpoint = service.endpoint;

  // Optimized: Build batch request in single pass, tracking empty indices
  const batchRequests: ({ endpoint: string; query: any } | null)[] = new Array(
    data.length
  );
  const emptyIndices = new Set<number>();

  for (let i = 0; i < data.length; i++) {
    const request = buildRowRequest(service, data[i], mapping, serviceOptions);
    batchRequests[i] = request;
    if (request === null) {
      emptyIndices.add(i);
    }
  }

  // Filter out empty requests (only allocate array for valid requests)
  const validRequests: { endpoint: string; query: any }[] = [];
  for (let i = 0; i < batchRequests.length; i++) {
    const req = batchRequests[i];
    if (req !== null) {
      validRequests.push(req);
    }
  }

  // Handle case where all requests are empty
  if (validRequests.length === 0) {
    return data.map((_, idx) => ({
      rowIndex: idx,
      fields: {},
      error: "No valid fields mapped",
      success: false,
    }));
  }

  // Prepare headers once (optimized for speed - no repeated object creation)
  const headers: HeadersInit = {
    "Content-Type": "application/json",
  };

  // Add authentication (pre-compute once)
  if (service.authentication) {
    if (
      service.authentication.type === "api_key" ||
      service.authentication.type === "bearer"
    ) {
      const apiKey = service.authentication.envVar
        ? import.meta.env[service.authentication.envVar]
        : null;
      if (apiKey) {
        headers[
          service.authentication.header || "Authorization"
        ] = `Bearer ${apiKey}`;
      }
    }
    // Domain-based auth handled by browser
  }

  // Add custom headers
  if (service.headers) {
    Object.assign(headers, service.headers);
  }

  // Pre-compute URL (avoid repeated string concatenation)
  const url = `${baseUrl}${endpoint}`;

  // Check if cancelled before making request
  if (options.abortSignal?.aborted) {
    return data.map((_, idx) => ({
      rowIndex: idx,
      fields: {},
      error: "Enhancement cancelled",
      success: false,
    }));
  }

  try {
    // Make API request
    // Use service configuration to determine request body format
    const requestFormat = service.batchRequestFormat || "object";
    const requestBody =
      requestFormat === "array"
        ? JSON.stringify(validRequests)
        : JSON.stringify({ bulkRequest: validRequests });

    const response = await fetch(url, {
      method: service.method,
      headers,
      body: requestBody,
      signal: options.abortSignal,
    });

    if (!response.ok) {
      const errorText = await response.text();
      const error = new Error(
        `Enhancement API error: ${response.status} - ${errorText}`
      );

      // Log to Sentry with context
      Sentry.captureException(error, {
        tags: {
          feature: "enhancement",
          operation: "executeEnhancement",
          serviceId: service.id,
          status: response.status.toString(),
        },
        contexts: {
          enhancement: {
            serviceId: service.id,
            serviceName: service.name,
            url,
            method: service.method,
            dataRows: data.length,
            validRequests: validRequests.length,
          },
        },
        extra: {
          errorText,
          responseStatus: response.status,
        },
      });

      // Return errors for all rows
      return data.map((_, idx) => {
        if (emptyIndices.has(idx)) {
          return {
            rowIndex: idx,
            fields: {},
            error: "No valid fields mapped",
            success: false,
          };
        }
        return {
          rowIndex: idx,
          fields: {},
          error: `API error: ${response.status} - ${errorText}`,
          success: false,
        };
      });
    }

    const batchResults = await response.json();

    // Check if cancelled after receiving response
    if (options.abortSignal?.aborted) {
      return data.map((_, idx) => ({
        rowIndex: idx,
        fields: {},
        error: "Enhancement cancelled",
        success: false,
      }));
    }

    // Optimized: Pre-build response fields cache for faster processing
    const responseFieldsCache = new Map<string, { apiResponsePath: string }>();
    // Pre-populate cache with field paths for all services
    const responseFields = service.responseFields;
    for (let i = 0; i < responseFields.length; i++) {
      const field = responseFields[i];
      responseFieldsCache.set(field.id, {
        apiResponsePath: field.apiResponsePath,
      });
    }

    // Process results and map back to original indices
    // batchResults is an array in the same order as validRequests
    const processedResults: EnhancementResult[] = new Array(data.length);
    let resultIdx = 0;

    for (let idx = 0; idx < data.length; idx++) {
      // Check if cancelled during processing
      if (options.abortSignal?.aborted) {
        // Fill remaining results with cancellation error
        for (let remainingIdx = idx; remainingIdx < data.length; remainingIdx++) {
          if (!processedResults[remainingIdx]) {
            processedResults[remainingIdx] = {
              rowIndex: remainingIdx,
              fields: {},
              error: "Enhancement cancelled",
              success: false,
            };
          }
        }
        return processedResults;
      }
      if (emptyIndices.has(idx)) {
        processedResults[idx] = {
          rowIndex: idx,
          fields: {},
          error: "No valid fields mapped",
          success: false,
        };
        continue;
      }

      if (resultIdx >= batchResults.length) {
        processedResults[idx] = {
          rowIndex: idx,
          fields: {},
          error: "Missing result from API",
          success: false,
        };
        continue;
      }

      const apiResponse = batchResults[resultIdx];
      const fields = processRowResponse(
        service,
        apiResponse,
        mapping,
        responseFieldsCache,
        serviceOptions
      );

      // Check for errors in processed response
      if (fields.error) {
        processedResults[idx] = {
          rowIndex: idx,
          fields: {},
          error: fields.error,
          success: false,
        };
        resultIdx++;
        continue;
      }

      processedResults[idx] = {
        rowIndex: idx,
        fields,
        success: true,
      };

      resultIdx++;
    }

    return processedResults;
  } catch (error) {
    // Check if error is due to cancellation
    if (error instanceof Error && error.name === "AbortError") {
      return data.map((_, idx) => ({
        rowIndex: idx,
        fields: {},
        error: "Enhancement cancelled",
        success: false,
      }));
    }

    // Log to Sentry with context
    const errorObj = error instanceof Error ? error : new Error(String(error));
    Sentry.captureException(errorObj, {
      tags: {
        feature: "enhancement",
        operation: "executeEnhancement",
        serviceId: service.id,
      },
      contexts: {
        enhancement: {
          serviceId: service.id,
          serviceName: service.name,
          url,
          method: service.method,
          dataRows: data.length,
        },
      },
      extra: {
        errorMessage: errorObj.message,
        errorStack: errorObj.stack,
      },
    });

    // Return errors for all rows
    return data.map((_, idx) => ({
      rowIndex: idx,
      fields: {},
      error: error instanceof Error ? error.message : "Unknown error",
      success: false,
    }));
  }
}

/**
 * Execute enhancement for data rows with caching and deduplication
 */
export async function executeEnhancement(
  service: EnhancementService,
  data: Record<string, any>[],
  mapping: EnhancementMapping,
  options: Omit<EnhancementExecutionOptions, "mapping"> = {}
): Promise<EnhancementResult[]> {
  const serviceOptions = options.serviceOptions || mapping.serviceOptions;
  const results: EnhancementResult[] = new Array(data.length);

  // Step 1: Check cache and deduplicate using automatic cache key generation
  const uniqueMap = new Map<
    string,
    {
      indices: number[];
      rowData: Record<string, any>;
    }
  >();

  for (let i = 0; i < data.length; i++) {
    // Check if cancelled during cache checking
    if (options.abortSignal?.aborted) {
      // Fill remaining results with cancellation error
      for (let remainingIdx = i; remainingIdx < data.length; remainingIdx++) {
        if (!results[remainingIdx]) {
          results[remainingIdx] = {
            rowIndex: remainingIdx,
            fields: {},
            error: "Enhancement cancelled",
            success: false,
          };
        }
      }
      return results;
    }

    const cacheKey = generateCacheKey(
      service,
      data[i],
      mapping,
      serviceOptions
    );

    if (!cacheKey) {
      // No cache key - add to unique map directly
      if (!uniqueMap.has(`_no_cache_${i}`)) {
        uniqueMap.set(`_no_cache_${i}`, {
          indices: [i],
          rowData: data[i],
        });
      } else {
        uniqueMap.get(`_no_cache_${i}`)!.indices.push(i);
      }
      continue;
    }

    const fullCacheKey = `${service.id}:${cacheKey}`;
    const cached = enhancementCache.get(fullCacheKey);

    if (cached) {
      // Cache hit - use immediately
      results[i] = {
        ...cached,
        rowIndex: i,
      };
      // Non-blocking progress update
      if (options.onProgress) {
        Promise.resolve().then(() => {
          const completed = results.filter((r) => r !== undefined).length;
          options.onProgress!(i, completed, data.length, results[i]);
        });
      }
    } else {
      // Cache miss - add to unique map
      if (!uniqueMap.has(fullCacheKey)) {
        uniqueMap.set(fullCacheKey, {
          indices: [i],
          rowData: data[i],
        });
      } else {
        // Duplicate - just track the index
        uniqueMap.get(fullCacheKey)!.indices.push(i);
      }
    }
  }

  // If everything was cached, return immediately
  if (uniqueMap.size === 0) {
    return results;
  }

  // Step 2: Build arrays for unique rows only
  const uniqueData: Record<string, any>[] = [];
  const cacheKeys: (string | null)[] = [];
  const uniqueIndices: number[][] = [];

  for (const [cacheKey, { rowData, indices }] of uniqueMap.entries()) {
    uniqueData.push(rowData);
    cacheKeys.push(cacheKey.startsWith("_no_cache_") ? null : cacheKey);
    uniqueIndices.push(indices);
  }

  // Step 3: Execute enhancement for unique rows
  if (!service.supportsBatch || uniqueData.length === 1) {
    // Execute single requests (not implemented yet, would iterate and call API)
    // For now, fall back to batch even for single items
  }

  const batchSize = service.maxBatchSize || 5000;
  const batches: Record<string, any>[][] = [];

  for (let i = 0; i < uniqueData.length; i += batchSize) {
    batches.push(uniqueData.slice(i, i + batchSize));
  }

  // Process batches sequentially to enable progress tracking
  let uniqueResultIndex = 0;
  for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
    // Check if cancelled before processing batch
    if (options.abortSignal?.aborted) {
      // Fill remaining results with cancellation error
      for (let i = 0; i < results.length; i++) {
        if (!results[i]) {
          results[i] = {
            rowIndex: i,
            fields: {},
            error: "Enhancement cancelled",
            success: false,
          };
        }
      }
      return results;
    }

    const batch = batches[batchIndex];
    const batchResults = await executeBatch(service, batch, mapping, options);

    // Check if cancelled after batch completes
    if (options.abortSignal?.aborted) {
      // Fill remaining results with cancellation error
      for (let i = 0; i < results.length; i++) {
        if (!results[i]) {
          results[i] = {
            rowIndex: i,
            fields: {},
            error: "Enhancement cancelled",
            success: false,
          };
        }
      }
      return results;
    }

    // Step 4: Map batch results back to all original positions and update cache
    for (const result of batchResults) {
      // Check if cancelled during result mapping
      if (options.abortSignal?.aborted) {
        // Fill remaining results with cancellation error
        for (let i = 0; i < results.length; i++) {
          if (!results[i]) {
            results[i] = {
              rowIndex: i,
              fields: {},
              error: "Enhancement cancelled",
              success: false,
            };
          }
        }
        return results;
      }

      if (uniqueResultIndex >= uniqueData.length) continue;

      const cacheKey = cacheKeys[uniqueResultIndex];
      const originalIndices = uniqueIndices[uniqueResultIndex];

      // Cache the result if we have a cache key
      if (cacheKey) {
        const fullCacheKey = `${service.id}:${cacheKey}`;
        enhancementCache.set(fullCacheKey, result);
      }

      // Distribute to all original indices that share this unique row
      for (const originalIndex of originalIndices) {
        results[originalIndex] = {
          ...result,
          rowIndex: originalIndex,
        };

        // Update progress as we process each result
        if (options.onProgress) {
          const completed = results.filter((r) => r !== undefined).length;
          options.onProgress!(
            originalIndex,
            completed,
            data.length,
            results[originalIndex]
          );
        }
      }

      uniqueResultIndex++;
    }
  }

  return results;
}
